import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from "@mui/material";
import { useState } from "react";
import type { TrainingModuleCategory, TrainingModuleDetail } from "@xop/dashboard-shared";

import { createTrainingModule } from "./api.js";
import { CATEGORY_LABEL } from "./trainingLogic.js";

/**
 * "New module" — just enough to create the row (title, category, summary).
 * Everything else is edited on the module's own page, where the caller
 * navigates once this resolves. A new module starts as a DRAFT: resellers see
 * nothing until someone flips Published.
 */
export function NewModuleDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (m: TrainingModuleDetail) => void;
}): JSX.Element {
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState<TrainingModuleCategory>("service");
  const [summary, setSummary] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = (): void => {
    setTitle("");
    setCategory("service");
    setSummary("");
    setError(null);
  };

  const submit = async (): Promise<void> => {
    if (!title.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const created = await createTrainingModule({
        title: title.trim(),
        category,
        ...(summary.trim() ? { summary: summary.trim() } : {}),
      });
      reset();
      onCreated(created);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not create the module");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!busy) {
          reset();
          onClose();
        }
      }}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>New training module</DialogTitle>
      <DialogContent>
        <Stack
          component="form"
          id="new-training-module"
          spacing={2}
          sx={{ pt: 1 }}
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label="Title"
            placeholder="Mass Notification"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            autoFocus
            required
            inputProps={{ "data-testid": "new-module-title" }}
          />
          <TextField
            select
            label="Kind"
            value={category}
            onChange={(e) => setCategory(e.target.value as TrainingModuleCategory)}
            helperText="Services are the products themselves; extensions add to one."
            SelectProps={{ native: true }}
            inputProps={{ "data-testid": "new-module-category" }}
          >
            {(Object.keys(CATEGORY_LABEL) as TrainingModuleCategory[]).map((c) => (
              <option key={c} value={c}>
                {CATEGORY_LABEL[c]}
              </option>
            ))}
          </TextField>
          <TextField
            label="Summary"
            placeholder="What a reseller will be able to do after watching it"
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            multiline
            minRows={2}
            inputProps={{ "data-testid": "new-module-summary" }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          color="inherit"
          disabled={busy}
          onClick={() => {
            reset();
            onClose();
          }}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          form="new-training-module"
          variant="contained"
          disabled={busy || !title.trim()}
          data-testid="new-module-create"
        >
          {busy ? "Creating…" : "Create module"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
